import { gql } from "@apollo/client";
import { useRef, useState } from "react";
import { useNavigate, useParams } from "react-router";
import styled from "styled-components";

import useCanEditBrand from "../../../../clients/users/useCanEditBrand";
import * as UI from "../../../../modules/ui-kit";
import Pricelists from "../../clients/Pricelists.graphql";
import useCreatePricelist from "../../clients/useCreatePricelist";
import useRecordPrices from "../../clients/useRecordPrices";
import CountrySelect from "../../components/CountrySelect";
import { parseXlsx, parsePriceRows, assemblePrices, TEMPLATE_URL } from "../../lib/importXlsx";
import { resolveProductIds } from "../../lib/resolveProducts";

import type { Currency } from "../../clients/usePricelists";


const Styled = styled.section`
    display: flex;
    flex-direction: column;
    gap: 24px;

    header {
        align-items: center;
        display: flex;
        gap: 16px;
        justify-content: space-between;
    }

    header span {
        color: #001;
        font-size: 2.4rem;
    }

    form {
        display: flex;
        flex-direction: column;
        gap: 16px;
    }

    label {
        display: flex;
        flex-direction: column;
        gap: 4px;
        font-size: 0.9rem;
    }

    input {
        padding: 6px 8px;
        font-size: 1rem;
        border: 1px solid #ccc;
    }

    button[type="submit"] {
        align-self: flex-start;
        cursor: pointer;
        padding: 8px 16px;
    }

    .error {
        color: red;
        font-size: 0.85rem;
    }
    .info {
        color: #333;
        font-size: 0.85rem;
    }
`;

const CURRENCY_OPTIONS: { label: string; value: Currency }[] = [
    { label: "EUR", value: "EUR" },
    { label: "CZK", value: "CZK" },
    { label: "CHF", value: "CHF" },
    { label: "RON", value: "RON" },
    { label: "RSD", value: "RSD" },
    { label: "HUF", value: "HUF" },
    { label: "BGN", value: "BGN" },
];

export default function PricelistNew() {
    const { brandID } = useParams();
    const navigate = useNavigate();
    const canEdit = useCanEditBrand(brandID);

    if (!canEdit) return <Styled><span>Not authorized to create price lists for this brand.</span></Styled>;

    const [name, setName] = useState("");
    const [currency, setCurrency] = useState<Currency>("EUR");
    const [countries, setCountries] = useState<string[]>([]);
    const country = countries[0] ?? "";
    const [validFrom, setValidFrom] = useState(() => isoDate(new Date()));
    const [validTo, setValidTo] = useState(() => {
        const d = new Date();
        d.setFullYear(d.getFullYear() + 1);
        return isoDate(d);
    });

    const [error, setError] = useState<string | null>(null);
    const [info, setInfo] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const fileRef = useRef<HTMLInputElement>(null);

    const [createPricelist] = useCreatePricelist();
    const [recordPrices] = useRecordPrices();

    async function handleSubmit(e?: React.FormEvent) {
        e?.preventDefault();
        const file = fileRef.current?.files?.[0];
        if (!file) {
            setError("Select an XLSX file");
            return;
        }
        if (!name.trim()) {
            setError("Name is required");
            return;
        }
        if (!country) {
            setError("Country is required");
            return;
        }

        setLoading(true);
        setError(null);
        setInfo(null);

        try {
            const rows = await parseXlsx(file);
            const { rows: parsed, failed } = parsePriceRows(rows);
            if (parsed.length === 0) {
                setError("XLSX has no valid rows. Check format: column A = manufacturer identifier, column F = price.");
                return;
            }

            // Resolve identifiers → product UUIDs before creating anything, so a
            // cancel at the "not found" prompt leaves no empty pricelist behind.
            const { idByValue } = await resolveProductIds(
                parsed.map((r) => r.identifier),
                brandID,
            );
            const matched = parsed.filter((r) => idByValue.has(r.identifier));
            const notFound = parsed.length - matched.length;

            if (matched.length === 0) {
                setError("No products matched the identifiers in column A for this brand.");
                return;
            }
            if (notFound > 0 && !confirm(
                `${notFound} of ${parsed.length} products were not found. Import the remaining ${matched.length}?`,
            )) return;

            const { data: createData } = await createPricelist({
                variables: {
                    pricelist: {
                        brandID,
                        name: [{ language: "en", text: name.trim() }],
                        currency,
                        countries,
                        valid:
                            validFrom || validTo
                                ? {
                                    from: validFrom || undefined,
                                    to: validTo || undefined,
                                }
                                : undefined,
                    },
                },
            });

            const pricelistID = createData.createPricelist.id;
            const { prices } = assemblePrices(pricelistID, parsed, idByValue);

            // Refetch the list AFTER prices are recorded so productCount is correct
            // (recording before this would show 0 products).
            await recordPrices({
                variables: { prices },
                refetchQueries: [
                    { query: gql(Pricelists), variables: { brandID } },
                ],
                awaitRefetchQueries: true,
            });

            setInfo(`Imported: ${prices.length} rows OK, ${failed} invalid, ${notFound} not found.`);
            navigate(`../${pricelistID}`, { relative: "path" });
        } catch (e) {
            setError(e.message);
        } finally {
            setLoading(false);
        }
    }

    return (
        <Styled>
            <header>
                <span>New Price List</span>
                <div className="actions">
                    <UI.Button
                        type="button"
                        $appearance="primary"
                        onClick={() => handleSubmit()}
                        disabled={loading}
                    >
                        {loading ? "Importing…" : "Create price list"}
                    </UI.Button>
                </div>
            </header>
            <form onSubmit={handleSubmit}>
                <label>
                    Name *
                    <input
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="e.g. Standard Prices 2026"
                    />
                </label>
                <label>
                    Currency
                    <UI.Select
                        value={currency}
                        onSelect={(v) => setCurrency(v as Currency)}
                        options={CURRENCY_OPTIONS}
                    />
                </label>
                <label>
                    Country *
                    <CountrySelect
                        value={countries}
                        onChange={setCountries}
                        disabled={loading}
                    />
                </label>
                <label>
                    Valid from
                    <input
                        type="date"
                        value={validFrom}
                        onChange={(e) => setValidFrom(e.target.value)}
                    />
                </label>
                <label>
                    Valid to
                    <input
                        type="date"
                        value={validTo}
                        onChange={(e) => setValidTo(e.target.value)}
                    />
                </label>
                <label>
                    XLSX file *
                    <input type="file" accept=".xlsx" ref={fileRef} />
                </label>
                <button
                    type="button"
                    onClick={() => window.open(TEMPLATE_URL, "_blank")}
                >
                    Download template
                </button>
                {error && <span className="error">{error}</span>}
                {info && <span className="info">{info}</span>}
            </form>
        </Styled>
    );
}

function isoDate(d: Date): string {
    return d.toISOString().slice(0, 10);
}
